<script setup lang="ts">
import { ref } from "vue";
import IconComponent from "../common/IconComponent.vue";
import DataTable from "../../table/DataTable.vue";
import { Filter } from "../../../types/report";
import { api } from "../../../api/Api";

const props = withDefaults(
  defineProps<{
    modelValue:
      | string
      | number
      | boolean
      | Date
      | boolean[]
      | string[]
      | number[]
      | Date[]
      | null;
    endPoint?: string;
    filter: Filter;
    label?: string;
    error?: string | null;
    readonly?: boolean;
    placeholder?: string;
    maxWidth?: string;
    multiple?: boolean;
    size?: "sm" | "md" | "lg";
  }>(),
  {
    maxWidth: "100%",
    size: "md",
    multiple: false,
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: number | string | null | undefined): void;
  (e: "select", data: any): void;
  (e: "blur"): void;
}>();

const showForm = ref(false);
const handleClick = () => {
  getData();
};

const data = ref<any[]>([]);
const filteredData = ref<any[]>([]);

const refreshData = ref(false);
const getData = async () => {
  const response = await api.post("clients/reports/list-data", {
    table: props.filter.dbTable,
    id_field: props.filter.dbIdField,
    name_field: props.filter.dbNameField,
  });
  if (response.success == true) {
    data.value = response.data;
    filteredData.value = response.data;
    refreshData.value = !refreshData.value; // Toggle to refresh DataTable
    showForm.value = true;
  }
};

const selectedData = ref<any | null | any[]>(null);

const search = ref("");
const handleSearch = () => {
  if (!search.value) {
    filteredData.value = data.value;
  } else {
    filteredData.value = data.value.filter((item) =>
      item[props.filter.dbNameField ?? ""]
        .toLowerCase()
        .includes(search.value.toLowerCase())
    );
  }
};

const handleInput = (e: Event) => {
  const input = e.target as HTMLInputElement;
  const value = input.value;

  emit("update:modelValue", value);
};

/*const handleSelect = () => {
  emit("update:modelValue", "");
};*/

const focused = ref(false);
const handleBlur = () => {
  focused.value = false;
  emit("blur");
  console.log("Blur event emitted");
};

import { watch } from "vue";

watch(
  () => props.modelValue,
  (newVal) => {
    if (
      newVal === null ||
      newVal === undefined ||
      newVal === "" ||
      (Array.isArray(newVal) && newVal.length === 0)
    ) {
      selectedData.value = null;
    }
  }
);

</script>

<template>
  <div :style="{ maxWidth: maxWidth, width: '100%' }">
    <label
      v-if="label"
      class="block text-sm font-medium text-gray-700 dark:text-gray-800 mb-1"
    >
      {{ label }}
    </label>

    <div
      class="relative flex rounded-lg"
      id="button-input"
      :style="{ outline: focused ? '1px solid #e5e7eb' : '' }"
      :class="[
        error
          ? 'border-red-300 dark:border-red-700'
          : 'border-gray-200 dark:border-gray-700',
      ]"
    >
      <!-- Input Field -->
      <input
        type="text"
        :value="selectedData ? selectedData[props.filter.dbNameField ?? ''] : ''"
        @input="handleInput"
        @focus="focused = true"
        @blur="handleBlur"
        :readonly="true"
        placeholder="Click to Arrow to view List"
        :style="{ maxWidth: maxWidth, width: '100%' }"
        class="flex-1 px-3 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-center focus:outline-none"
        :class="[
          readonly
            ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed'
            : 'bg-white dark:bg-gray-900',
          size == 'sm' ? 'py-1' : 'py-2',
        ]"
      />

      <!-- handleClick Button -->
      <button
        type="button"
        @click="handleClick"
        @focus="focused = true"
        @blur="focused = false"
        tabindex="-1"
        :disabled="readonly"
        class="flex-none px-3 border border-gray-200 dark:border-gray-700 dark:text-black border-l-0 rounded-r-lg transition-colors"
        :class="[size == 'sm' ? 'py-1' : 'py-2']"
      >
        <IconComponent name="MousePointerClick" />
      </button>
    </div>

    <!-- Error message -->
    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">
      {{ error }}
    </p>

    <div
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-auto"
      v-if="showForm"
    >
      <div
        ref="modalRef"
        class="bg-white dark:bg-gray-800 shadow-xl flex flex-col"
        style="max-height: 450px; width: 100%; max-width: 600px; overflow: auto"
      >
        <!-- Fixed Header -->
        <div
          class="bg-white dark:bg-gray-800 px-5 py-3 border-b border-gray-200 dark:border-gray-700"
        >
          <div class="flex items-center justify-between w-full">
            <span class="font-semibold text-lg">
              Select {{ filter.caption }}
            </span>
            <button
              @click="showForm = false"
              class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 ml-2"
              aria-label="Close"
            >
              <IconComponent name="LogOut" />
            </button>
          </div>
          <div class="s">
            <IconComponent
                name="Search"
                class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="search"
                v-model="search"
                @keyup="handleSearch"
                placeholder="Search..."
                class="w-full pl-10 pr-10 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
              <button
                v-if="search"
                @mouseenter="search = ''"
                @focus="search = ''"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
              >
                <IconComponent name="Trash2" class="w-4 h-4" />
              </button>
          </div>
        </div>

        <div class="pr-5 pl-5">
          <DataTable
            :title="label ?? ''"
            :fields="[
              {
                name: props.filter.dbNameField ?? '',
                caption: props.filter.dbNameField ?? '',
                sortable: true,
                fieldType: 'text',
              },
            ]"
            :rows="filteredData"
            :actions="[
              {
                name: 'select',
                permission: 'view',
                icon: 'MousePointer2',
                variant: 'primary',
                title: 'Select',
                visible: () => true,
                ignorePermission: true,
                hideMobile: true,
              },
            ]"
            :endPoint="endPoint"
            @search="handleSearch"
            @action="
              (action, data) => {
                if (action === 'select') {
                  if (multiple) {
                    if (!Array.isArray(selectedData)) {
                      selectedData = [];
                    }
                    selectedData.push(data);
                    console.log('data', data);
                    emit('update:modelValue', selectedData);
                    emit('select', selectedData);
                    console.log('Selected Data:', selectedData);
                  } else {
                    selectedData = data
                    emit('update:modelValue', data[props.filter.dbIdField ?? '']);
                    emit('select', data);
                    showForm = false;
                  }
                }
              }
            "
            @row-dbl-click="
              (data) => {
                if (multiple) {
                    if (!Array.isArray(selectedData)) {
                      selectedData = [];
                    }
                    selectedData.push(data);
                    emit('update:modelValue', selectedData);
                    emit('select', selectedData);
                  } else {
                    selectedData = data
                    emit('update:modelValue', data[props.filter.dbIdField ?? '']);
                    emit('select', data);
                    showForm = false;
                  }
              }
            "
            :show-header="false"
            :show-search="false"
            :refresh-data="refreshData"
            :show-filter="false"
            :showEdit="false"
            :showDelete="false"
            :showAdd="false"
            :showSelect="true"
          />
        </div>
      </div>
    </div>
  </div>
</template>
